import React, { useEffect, useMemo, useState } from "react";
import { getAgentEndpoints } from "@/api/chatbot";
import { AgentEndpoint } from "@/types/navigation";
import { getVenueShortNameById } from "@/utils/map-util";
import { useNavigationStore } from "@/store/navigationStore";
import Image from "../image";
import ImageButton from "../button/image-button";
import { useAppStore } from "@/store/appStore";

type AgentChatDropdownProps = {
  disabled?: boolean;
  className?: string;
};

const getAgentKey = (agent: AgentEndpoint) => `${agent.venueId}:${agent.version}`;

const AgentChatDropdown: React.FC<AgentChatDropdownProps> = ({
  disabled = false,
  className = "",
}) => {
  const setChatAgentEndpoint = useNavigationStore(
    (s) => s.setChatAgentEndpoint,
  );
  const setChatAgentVenueId = useNavigationStore((s) => s.setChatAgentVenueId);

  const [agentEndpoints, setAgentEndpoints] = useState<AgentEndpoint[]>([]);
  const [selectedAgentKey, setSelectedAgentKey] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const debugMode = useAppStore((state) => state.debugMode);

  const agentKeyToAgent = useMemo(() => {
    const map = new Map<string, AgentEndpoint>();
    for (const agent of agentEndpoints) {
      map.set(getAgentKey(agent), agent);
    }
    return map;
  }, [agentEndpoints]);

  useEffect(() => {
    const fetchAgents = async () => {
      const endpoints = await getAgentEndpoints();
      setAgentEndpoints(endpoints);
      if (endpoints.length > 0) {
        const firstReady = endpoints.find((e) => e.ready) ?? endpoints[0];
        const key = getAgentKey(firstReady);
        setSelectedAgentKey(key);
        setChatAgentEndpoint(firstReady.endpoint);
        setChatAgentVenueId(firstReady.venueId);
      }
    };

    fetchAgents().catch((e) => {
      console.warn("Failed to fetch agent endpoints", e);
    });
  }, [debugMode, setChatAgentEndpoint, setChatAgentVenueId]);

  const getAgentLabel = (agent: AgentEndpoint) => {
    const venue = getVenueShortNameById(agent.venueId) || agent.venueId;
    return `${venue} : ${agent.version} ${agent.ready ? '' : '(WIP)'}`;
  };

  const selectedAgent = agentKeyToAgent.get(selectedAgentKey);
  const selectedLabel = selectedAgent ? getAgentLabel(selectedAgent) : selectedAgentKey;


  return (
    <>
      {debugMode && <button
        type="button"
        className={`w-full h-[38px]  px-2 rounded-full bg-black flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        disabled={disabled || agentEndpoints.length === 0}
        onClick={() => setIsOpen(true)}
        aria-label="Select agent"
      >
        <span className="w-[100px] truncate text-sm primary-text-color">
          {selectedLabel}
        </span>

        <Image
          aria-hidden
          src="/assets/icon_chevron_down.png"
          alt="open selector"
          width={18}
          height={18}
        />
      </button>}

      {isOpen ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-paper-gray rounded-lg w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4 px-[20px] pt-[20px]">
              <h2 className="text-xl font-semibold primary-text-color">Agent</h2>
              <ImageButton
                onClick={() => setIsOpen(false)}
                src="/assets/icon_close.png"
                alt="close"
                size={20}
                className="text-gray-500 hover:text-gray-700"
              />
            </div>

            <div className="flex-1 overflow-y-auto">
              {agentEndpoints.map((agent) => {
                const key = getAgentKey(agent);
                const label = getAgentLabel(agent);
                const isSelected = selectedAgentKey === key;
                const isDisabled = !agent.ready;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      setSelectedAgentKey(key);
                      setChatAgentEndpoint(agent.endpoint);
                      setChatAgentVenueId(agent.venueId);
                      setIsOpen(false);
                    }}
                    className={`px-[20px] w-full text-left p-3 rounded flex items-center justify-between ${isSelected ? "bg-green-light" : ""
                      } disabled:cursor-not-allowed`}
                  >
                    <div className={`font-medium ${isDisabled ? "text-gray-400" : "primary-text-color"}`}>{label}</div>
                    {isSelected ? (
                      <Image
                        aria-hidden
                        src="/assets/icon_check.png"
                        alt="selected"
                        width={20}
                        height={20}
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AgentChatDropdown;

